/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import NodeCollectionEffectBase from './NodeCollectionEffectBase';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Effect for triggering a Cocos animation.
 */
@cc._decorator.ccclass
export default class CocosAnimationEffect extends NodeCollectionEffectBase
{
    /** The [[cc.Animation]] to be animated. */
    @property({ type: [cc.Animation], visible: true })
    protected animations: cc.Animation[] = [];

    /** The animation name to play on the [[cc.Animation]]. */
    @property()
    protected animationName: string = '';
    public set AnimationName(value: string)
    {
        this.animationName = value;
    }

    /** Whether or not the animation should loop. */
    @property()
    protected loop: boolean = false;

    /**
     * Whether or not the [[cc.Node]] of the [[cc.Animation]] should be set active
     * before playing the animation.
     */
    @property()
    protected setActive: boolean = false;

    /** Should the animation track be cleared on complete. */
    @property()
    protected clearOnComplete: boolean = true;

    /** Should the [[cc.Animation]] be reset to its start position on complete. */
    @property()
    protected resetPosOnComplete: boolean = true;

    @property(cc.Float)
    protected speed: number = 1;

    protected finishedAnims: number = 0;

    protected onInitStateChange(): boolean
    {
        if (!super.onInitStateChange()) { return false; }

        if (!this.animations.toString())
        {
            this.reference.WithValue((nodeCollection) =>
            {
                nodeCollection.Nodes.forEach((node) => { this.animations.push(node.getComponent(cc.Animation)); });
            });
        }

        return true;
    }

    /** Activates the effect. */
    protected trigger = (): void =>
    {
        if (this.setActive) { this.animations.forEach((anim) => { anim.node.active = true; }); }

        if (this.animationName === '')
        {
            this.Stop();
        }
        else
        {
            this.play();
        }
    }

    /** @hidden */
    private play(): void
    {
        this.finishedAnims = 0;
        let animationStates: cc.AnimationState[] = [];
        this.animations.forEach((anim) =>
        {
            animationStates.push(anim.play(this.animationName));
            anim.once('finished', (event: cc.Event.EventCustom) =>
            {
                if (!animationStates.some(value => value === event.currentTarget)) { return; }
                else { this.finishedAnims++; }

                if (this.finishedAnims === animationStates.length) { this.onAnimEnd(); }
            });
        });

        animationStates.forEach((animState) =>
        {
            if (!this.loop) { animState.wrapMode = cc.WrapMode.Normal; }
            animState.speed = this.dtModRef.Value.Multiply(this.speed);
        });

        if (animationStates.length !== this.animations.length)
        {
            CLog.error(`Could not find animation: [${this.animationName}].`, this, LogTypes.Effect);
            return;
        }
    }

    /** Stops the in-progress effect or stops its next execution. */
    public Stop(): void
    {
        super.Stop();

        if (this.IsCurrentState())
        {
            this.onAnimEnd();
        }
    }

    /** @hidden */
    private onAnimEnd = (): void =>
    {
        this.animations.forEach((anim) =>
        {
            if (this.clearOnComplete)
            {
                anim.stop();
            }

            if (this.setActive)
            {
                anim.node.active = false;
            }

            if (this.resetPosOnComplete)
            {
                anim.setCurrentTime(0);
            }
        });

        this.complete();
    }
}
